/**
 * Charts in exported answers ("Export as HTML").
 *
 * The export is a standalone HTML file, so it can't run recharts. Instead it
 * copies the charts already drawn on screen for the answer: each chart
 * panel is cloned without its hover tooltip, under a plain-text title. recharts
 * draws with inline SVG attributes, so the copy renders without app CSS.
 */

/** Wrapper id for the charts drawn under message `index`. */
export function answerChartsElementId(index: number): string {
  return `answer-charts-${index}`;
}

/** Styles for the copied charts inside the exported page. */
export const EXPORTED_CHART_CSS = `
        .finn-chart {
            margin: 20px 0;
            padding: 16px;
            border: 1px solid #e6e5e0;
            border-radius: 8px;
            background: #fcfcfb;
        }
        .finn-chart-title {
            margin: 0 0 8px;
            font-size: 15px;
            font-weight: 600;
            color: #0b0b0b;
        }
        .finn-chart svg {
            max-width: 100%;
            height: auto;
        }
        .finn-chart .recharts-legend-wrapper {
            position: static !important;
            width: auto !important;
        }`;

/**
 * HTML for the charts drawn under message `index`, or "" when it has none.
 * Reads the live DOM, so call it while the answer is on screen.
 */
export function exportedChartsHtml(
  index: number,
  doc: Document = document
): string {
  const host = doc.getElementById(answerChartsElementId(index));
  if (!host) return "";
  const figures = Array.from(
    host.querySelectorAll<HTMLElement>('[role="figure"]')
  );
  return figures
    .map((figure) => {
      const clone = figure.cloneNode(true) as HTMLElement;
      clone
        .querySelectorAll(".recharts-tooltip-wrapper")
        .forEach((el) => el.remove());
      // Only the chart panels are copied; the title is re-added as plain text
      // (the on-screen header row holds the PNG button).
      const title = figure.getAttribute("aria-label") || "Chart";
      const panels = Array.from(
        clone.querySelectorAll<HTMLElement>(".recharts-responsive-container")
      )
        .map((panel) => panel.innerHTML)
        .join("");
      return (
        `<figure class="finn-chart">` +
        `<h3 class="finn-chart-title">${escapeHtml(title)}</h3>` +
        panels +
        `</figure>`
      );
    })
    .join("\n");
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
